/**
 * 动效调度器（§11.6）。
 *
 * 三条不可让步的约束，整个文件的结构都是为它们服务的：
 *  1. 红线 #46 —— 只改 `transform` / `opacity`；`will-change` 只在 `.is-animating` 期间挂。
 *  2. 红线 #47 —— 启动动画时**零** `getBoundingClientRect`：所有坐标来自
 *     `useTableGeometry` 缓存的 `TableLayout`（`layoutRef.current`）。
 *  3. 红线 #50 —— `fastForward()` 只允许在「轮到自己」时被调用；这里只提供方法，
 *     调用时机的判定在 `useTableFx` 里，且带注释说明为什么不能无条件快进。
 *
 * 组件通过 `register(key, el)` 把自己的可动节点登记进来（ref 回调里做，零查询）。
 * 动画只认 key，不认 DOM 结构，于是组件重构不会悄悄让动画失效——找不到节点就跳过。
 */

import type { SeatIndex } from '@poker/shared'

import type { Point, TableLayout } from '../table/seatGeometry.js'

import { ChipSpritePool, SPRITE_SIZE } from './chipSprites.js'
import {
  bubbleFrames,
  enterFrames,
  flightFrames,
  flipFrames,
  flipTranslateFrames,
  glowFrames,
  seatFlipDeltas,
} from './flip.js'
import {
  BUBBLE_TOTAL_MS,
  FULL_MOTION,
  GATES,
  MOTION,
  resolveTimings,
  spriteCountFor,
  type MotionProfile,
} from './motion.js'

// ============================================================================
// 节点 key
// ============================================================================

/** 全部节点 key 的唯一出处。组件与动画靠它对齐，拼字符串一律走这里。 */
export const fxKey = {
  seat: (seat: SeatIndex): string => `seat:${seat}`,
  glow: (seat: SeatIndex): string => `glow:${seat}`,
  bubble: (seat: SeatIndex): string => `bubble:${seat}`,
  /** 座位底牌第 i 张（i = 0 | 1） */
  hole: (seat: SeatIndex, i: number): string => `hole:${seat}:${i}`,
  /** hero 手牌第 i 张 */
  hero: (i: number): string => `hero:${i}`,
  /** 公共牌第 i 张（0..4） */
  board: (i: number): string => `board:${i}`,
  /** 座位下注筹码堆 */
  bet: (seat: SeatIndex): string => `bet:${seat}`,
  pot: (): string => 'pot',
  /** 牌的 3D 翻转内层（`.card__inner`） */
  inner: (key: string): string => `${key}:inner`,
} as const

type Gate = 'card' | 'sprite'

/** 精灵散开偏移：固定表而非随机数——红线 #15 禁 Math.random，且确定性让回放可复现 */
const SPREAD: readonly Point[] = [
  { x: 0, y: 0 },
  { x: -7, y: 3 },
  { x: 6, y: -4 },
  { x: -3, y: -7 },
  { x: 8, y: 5 },
]

export interface FxControllerOptions {
  /** 几何缓存。动画期间只读它，绝不读 DOM。 */
  readonly getLayout: () => TableLayout | null
  readonly getProfile: () => MotionProfile
}

export interface ChipFlight {
  readonly seat: SeatIndex
  readonly amount: number
}

export class FxController {
  private readonly opts: FxControllerOptions
  private readonly nodes = new Map<string, HTMLElement>()
  private readonly running = new Set<Animation>()
  private layer: HTMLElement | null = null
  private pool: ChipSpritePool | null = null
  private busy: Record<Gate, number> = { card: 0, sprite: 0 }
  private readonly bubbleSubs = new Set<(seat: SeatIndex, ms: number) => void>()

  constructor(opts: FxControllerOptions) {
    this.opts = opts
  }

  // ---------------------------------------------------------------- 生命周期

  setLayer(el: HTMLElement | null): void {
    if (this.layer === el) return
    this.pool?.destroy()
    this.pool = null
    this.layer = el
    if (el !== null) this.pool = new ChipSpritePool(el)
  }

  register(key: string, el: HTMLElement): void {
    this.nodes.set(key, el)
  }

  unregister(key: string, el?: HTMLElement): void {
    // 带 el 时做身份校验：React 的 ref 回调在换 key 重挂时会先挂新的再卸旧的，
    // 不校验就会把刚注册好的新节点删掉。
    if (el !== undefined && this.nodes.get(key) !== el) return
    this.nodes.delete(key)
  }

  node(key: string): HTMLElement | null {
    return this.nodes.get(key) ?? null
  }

  private profile(): MotionProfile {
    return this.opts.getProfile()
  }

  private layout(): TableLayout | null {
    return this.opts.getLayout()
  }

  // ---------------------------------------------------------------- 运行控制

  /**
   * 全部动画立即跳到终态。★ 红线 #50：只有「轮到自己」才允许调用——
   * 无条件快进会让 AI 连打时每个动画都被下一个事件吃掉。
   */
  fastForward(): void {
    for (const a of [...this.running]) {
      try {
        a.finish()
      } catch {
        // 无限循环动画 finish() 会抛；这里没有无限动画，纯防御
        a.cancel()
      }
    }
  }

  /** 全部取消并回到初态。视角切换（传手机换人）必须走这条（§11.8 第 10 条）。 */
  cancelAll(): void {
    for (const a of [...this.running]) a.cancel()
    this.running.clear()
    this.busy = { card: 0, sprite: 0 }
  }

  get activeCount(): number {
    return this.running.size
  }

  /**
   * 闸门额度预留。返回 false 表示这次不播动画（调用方走瞬移分支）。
   *
   * 闸门口径是「**同时**在动的元素数」。带 stagger 的批量动画必须按峰值并发预留、
   * 而不是按元素个数：8 人桌发 16 张牌，逐张 +1 会在第 9 张就撞上 ≤8 的闸门，
   * 后半桌的牌直接瞬移——可真实并发只有 ceil(220/55)=4 张，从来没超标过。
   */
  private reserve(gate: Gate, n: number): boolean {
    if (n <= 0) return false
    if (this.busy[gate] + n > GATES[gate]) return false
    this.busy[gate] += n
    return true
  }

  private release(gate: Gate, n: number): void {
    this.busy[gate] = Math.max(0, this.busy[gate] - n)
  }

  /** 带 stagger 的批量动画的峰值并发数 */
  private static peakOf(count: number, duration: number, stagger: number): number {
    if (count <= 1 || stagger <= 0) return count
    return Math.min(count, Math.max(1, Math.ceil(duration / stagger)))
  }

  /**
   * `el.animate` 的唯一出口（不含闸门）。负责 `.is-animating` 的挂摘与 running 登记。
   * `will-change` 只在这个 class 存在期间生效（红线 #46）。
   */
  private animate(
    el: HTMLElement,
    frames: Keyframe[],
    timing: KeyframeAnimationOptions,
    onDone?: () => void,
  ): Animation | null {
    const duration = typeof timing.duration === 'number' ? timing.duration : 0
    if (duration <= 0) return null
    if (typeof el.animate !== 'function') return null

    el.classList.add('is-animating')
    const anim = el.animate(frames, { fill: 'none', ...timing })
    this.running.add(anim)
    const done = (): void => {
      this.running.delete(anim)
      el.classList.remove('is-animating')
      onDone?.()
    }
    // cancel() 会 reject，两个分支都要收尾，否则闸门额度会泄漏成永久占用
    void anim.finished.then(done, done)
    return anim
  }

  /** 单个动画：预留 1 个额度 */
  private run(
    el: HTMLElement,
    frames: Keyframe[],
    timing: KeyframeAnimationOptions,
    gate: Gate,
  ): Animation | null {
    if (!this.reserve(gate, 1)) return null
    const anim = this.animate(el, frames, timing, () => this.release(gate, 1))
    if (anim === null) this.release(gate, 1)
    return anim
  }

  /**
   * 批量动画：整批按峰值并发预留一次额度，最后一个动画结束时归还。
   * 额度不够就整批不播（而不是播一半、剩下的瞬移——那比全不播更难看）。
   */
  private runBatch(
    items: readonly { el: HTMLElement; frames: Keyframe[]; timing: KeyframeAnimationOptions }[],
    gate: Gate,
    peak: number,
  ): void {
    if (items.length === 0) return
    if (!this.reserve(gate, peak)) return
    let pending = 0
    const settle = (): void => {
      pending -= 1
      if (pending === 0) this.release(gate, peak)
    }
    for (const it of items) {
      const anim = this.animate(it.el, it.frames, it.timing, settle)
      if (anim !== null) pending += 1
    }
    if (pending === 0) this.release(gate, peak)
  }

  // ---------------------------------------------------------------- 发牌

  /**
   * 发牌飞入：每张 220ms，stagger 55ms，`translate(deck→target) + rotate(-14→0) + scale(.7→1)`。
   * lite 模式不 rotate（§11.6）。
   */
  dealCards(keys: readonly string[]): void {
    const layout = this.layout()
    if (layout === null) return
    const p = this.profile()
    const t = resolveTimings(MOTION.deal, p)
    if (t.duration <= 0) return
    const items = keys.flatMap((key, i) => {
      const el = this.node(key)
      const to = el === null ? null : this.anchorOf(key, layout)
      if (el === null || to === null) return []
      return [
        {
          el,
          frames: enterFrames(layout.deck, to, {
            rotate: p.lite ? 0 : MOTION.deal.rotate,
            scale: MOTION.deal.scale,
            fadeIn: true,
          }),
          timing: { duration: t.duration, delay: i * t.stagger, easing: t.easing },
        },
      ]
    })
    this.runBatch(items, 'card', FxController.peakOf(items.length, t.duration, t.stagger))
  }

  /** 翻牌 3D：300ms，翻牌圈 3 张 stagger 90ms */
  flipCards(keys: readonly string[]): void {
    const p = this.profile()
    const t = resolveTimings(MOTION.flip, p)
    if (t.duration <= 0) return
    const items = keys.flatMap((key, i) => {
      const el = this.node(fxKey.inner(key))
      if (el === null) return []
      return [
        {
          el,
          frames: flipFrames(),
          timing: { duration: t.duration, delay: i * t.stagger, easing: t.easing },
        },
      ]
    })
    this.runBatch(items, 'card', FxController.peakOf(items.length, t.duration, t.stagger))
  }

  /**
   * key → 该节点的终点坐标。发牌动画需要知道「牌最终停在哪」，
   * 而这只能来自布局缓存（红线 #47），绝不能去问 DOM。
   */
  private anchorOf(key: string, layout: TableLayout): Point | null {
    if (key.startsWith('board:')) {
      const slot = layout.board.slots[Number(key.slice('board:'.length))]
      // ★ Rect 的 x/y 是左上角，动画要的是中心，不能直接把 Rect 当 Point 用
      return slot === undefined ? null : { x: slot.cx, y: slot.cy }
    }
    if (key.startsWith('hero:')) {
      return layout.heroHand === null ? null : { x: layout.heroHand.cx, y: layout.heroHand.cy }
    }
    if (key.startsWith('hole:')) {
      const seat = Number(key.slice('hole:'.length).split(':')[0])
      return layout.seatBySeat.get(seat)?.center ?? null
    }
    return null
  }

  // ---------------------------------------------------------------- 筹码

  /** 下注筹码飞出：座位 → 下注位，260ms，scale(.6→1) */
  flyBet(seat: SeatIndex, amount: number, bigBlind: number): void {
    const layout = this.layout()
    const anchor = layout?.seatBySeat.get(seat)
    if (layout === null || anchor === undefined) return
    const p = this.profile()
    const n = spriteCountFor(amount, bigBlind, p.lite)
    if (n === 0) return
    this.flyChips(anchor.center, anchor.bet, n, {
      duration: MOTION.bet.duration,
      easing: MOTION.bet.easing,
      fromScale: MOTION.bet.fromScale,
      toScale: 1,
    })
  }

  /**
   * 筹码飞向底池：320ms，**按离池远近 stagger 40ms**，末 80ms 淡出。
   * 近的先到，视觉上像被池子「吸」过去；按座位号排序会让远近乱跳。
   */
  collectToPot(flights: readonly ChipFlight[], bigBlind: number): void {
    const layout = this.layout()
    if (layout === null || flights.length === 0) return
    const p = this.profile()
    const t = resolveTimings(MOTION.collect, p)
    if (t.duration <= 0) return
    const pot: Point = { x: layout.pot.cx, y: layout.pot.cy }
    const ordered = flights
      .map((f) => {
        const a = layout.seatBySeat.get(f.seat)
        const from = a?.bet ?? pot
        return { ...f, from, dist: Math.hypot(from.x - pot.x, from.y - pot.y) }
      })
      .sort((a, b) => a.dist - b.dist)

    ordered.forEach((f, i) => {
      const n = spriteCountFor(f.amount, bigBlind, p.lite)
      if (n === 0) return
      this.flyChips(f.from, pot, n, {
        duration: t.duration,
        easing: t.easing,
        delay: i * t.stagger,
        fromScale: 1,
        toScale: MOTION.collect.toScale,
        fadeTailMs: MOTION.collect.fadeTail,
      })
    })
  }

  /** 未跟注退还：下注位 → 座位，260ms，scale(1→.7)。原设计完全缺失的一条。 */
  returnUncalled(seat: SeatIndex, amount: number, bigBlind: number): void {
    const layout = this.layout()
    const anchor = layout?.seatBySeat.get(seat)
    if (anchor === undefined) return
    const p = this.profile()
    const n = spriteCountFor(amount, bigBlind, p.lite)
    if (n === 0) return
    this.flyChips(anchor.bet, anchor.center, n, {
      duration: MOTION.uncalled.duration,
      easing: MOTION.uncalled.easing,
      fromScale: 1,
      toScale: MOTION.uncalled.toScale,
    })
  }

  /** 底池推给赢家：420ms，多赢家间隔 200ms */
  pushPotTo(awards: readonly ChipFlight[], bigBlind: number): void {
    const layout = this.layout()
    if (layout === null || awards.length === 0) return
    const p = this.profile()
    const t = resolveTimings(MOTION.push, p)
    if (t.duration <= 0) return
    const pot: Point = { x: layout.pot.cx, y: layout.pot.cy }
    awards.forEach((a, i) => {
      const anchor = layout.seatBySeat.get(a.seat)
      if (anchor === undefined) return
      const n = spriteCountFor(a.amount, bigBlind, p.lite)
      if (n === 0) return
      this.flyChips(pot, anchor.center, n, {
        duration: t.duration,
        easing: t.easing,
        delay: i * MOTION.push.gap,
        fromScale: 1,
        toScale: MOTION.push.toScale,
      })
    })
  }

  private flyChips(
    from: Point,
    to: Point,
    count: number,
    o: {
      duration: number
      easing: string
      delay?: number
      fromScale?: number
      toScale?: number
      fadeTailMs?: number
    },
  ): void {
    const pool = this.pool
    if (pool === null) return
    for (let i = 0; i < count; i++) {
      const sprite = pool.acquire()
      if (sprite === null) return // 池空：直接少飞几个，绝不新建节点
      const j = SPREAD[i % SPREAD.length] ?? SPREAD[0] ?? { x: 0, y: 0 }
      const frames = flightFrames(
        { x: from.x + j.x, y: from.y + j.y },
        { x: to.x + j.x * 0.35, y: to.y + j.y * 0.35 },
        {
          size: SPRITE_SIZE,
          ...(o.fromScale === undefined ? {} : { fromScale: o.fromScale }),
          ...(o.toScale === undefined ? {} : { toScale: o.toScale }),
          ...(o.fadeTailMs === undefined ? {} : { fadeTailMs: o.fadeTailMs }),
          durationMs: o.duration,
        },
      )
      const timing: KeyframeAnimationOptions = {
        duration: o.duration,
        easing: o.easing,
        delay: (o.delay ?? 0) + i * 22,
      }
      const anim = this.run(sprite, frames, timing, 'sprite')
      if (anim === null) {
        pool.release(sprite)
        return
      }
      const back = (): void => pool.release(sprite)
      void anim.finished.then(back, back)
    }
  }

  // ---------------------------------------------------------------- 高亮 / 气泡

  /** 赢家高亮：光晕 2×600ms，只动预置元素的 opacity/scale。lite 模式不脉冲。 */
  winnerGlow(seats: readonly SeatIndex[]): void {
    const p = this.profile()
    if (p.reduced || p.lite) return
    for (const seat of seats) {
      const el = this.node(fxKey.glow(seat))
      if (el === null) continue
      this.run(
        el,
        glowFrames(MOTION.winner.scale),
        {
          duration: MOTION.winner.duration,
          iterations: MOTION.winner.iterations,
          easing: 'ease-in-out',
        },
        'card',
      )
    }
  }

  /**
   * 行动气泡**真的开播**时通知（座位、时长）。聊天气泡据此给它让位：
   * 「跟注 200」是牌局信息，同一座位上的「劳资不信」先让开、播完再回来。
   * 减弱动效下行动气泡根本不播，也就不通知 —— 聊天没有必要让一个不存在的东西。
   */
  onActionBubble(cb: (seat: SeatIndex, ms: number) => void): () => void {
    this.bubbleSubs.add(cb)
    return () => {
      this.bubbleSubs.delete(cb)
    }
  }

  /** 行动气泡：in 160 / hold 1200 / out 200 */
  showBubble(seat: SeatIndex): void {
    const el = this.node(fxKey.bubble(seat))
    if (el === null) return
    const p = this.profile()
    if (p.reduced) return
    const total = p.lite ? BUBBLE_TOTAL_MS - 300 : BUBBLE_TOTAL_MS
    for (const cb of this.bubbleSubs) cb(seat, total)
    this.run(
      el,
      bubbleFrames(total, MOTION.bubble.in, MOTION.bubble.out),
      { duration: total, easing: MOTION.bet.easing },
      'card',
    )
  }

  // ---------------------------------------------------------------- 座位重排

  /**
   * 座位重排 FLIP：200ms。First 来自上一份布局对象而不是 DOM 量测——
   * 这是本项目「零 getBoundingClientRect」还能做 FLIP 的关键。
   */
  reflowSeats(prev: ReadonlyMap<SeatIndex, Point>, next: ReadonlyMap<SeatIndex, Point>): void {
    const t = resolveTimings(MOTION.reseat, this.profile())
    if (t.duration <= 0) return
    for (const f of seatFlipDeltas(prev, next)) {
      const el = this.node(fxKey.seat(f.seat))
      if (el === null) continue
      this.run(
        el,
        flipTranslateFrames(f.dx, f.dy),
        { duration: t.duration, easing: t.easing },
        'card',
      )
    }
  }
}

/** 便于在没有 Provider 的环境（测试、Storybook）下拿到一个不会崩的实例 */
export function createNullFxController(): FxController {
  return new FxController({ getLayout: () => null, getProfile: () => FULL_MOTION })
}
